// Settings > Printing Setup types + option lists (ShipStation pattern).
// M3 Stage 4 SPEC.md Faz 3 (Orders + Shipping print sub-cluster) ratifies
// real PDF generation + label rendering + printer integration.
//
// Volkan direction 2026-05-23 (post-mock-UI iteration, 4 reference screenshots):
//   "yine Settings > Printing Setup. Orada sonsuz seçenek sunmamıza gerek yok
//    ama printing seçenekleri olması lazım. örnek sayfa atıyorum: aynı bu
//    opsiyonları yap."
//
// Schema hints (DRAFT — Stage 4 SPEC.md Faz 3 print sub-cluster ratify):
//   document_type_preferences  → per-account routing prefs (one row per type)
//   label_layout_settings      → label printer page layout + DPI + cutoff
//   packing_slip_settings      → packing slip rendering toggles
//   order_summary_settings     → order summary rendering toggles
//   print_warning_settings     → reprint warning toggles (per-machine)
//
// All types live here so the FE component and the MSW handler share a single
// source of truth; component shapes display labels, handler stores state.
// The default payload seed stays in mocks/data/printing-settings.ts
// (OPT-20261031-05).

// ---------------------------------------------------------------------------
// Enums — narrow string literal unions for type safety + form select binding.
// ---------------------------------------------------------------------------

export type PrintTo = 'always_prompt' | 'default_printer' | 'specific_printer'

export type LabelSize = '4x6' | '8.5x11' | 'A4' | 'A6'

export type LabelLayout =
  | '4x6_label_only'
  | '4x6_label_with_packing_slip'
  | '8.5x11_label'
  | 'A4'
  | 'A6'

export type LabelDPI = '203' | '300' | '600'

export type BatchSummaryOption = 'none' | 'start' | 'end'

// How a bulk print run is ordered — labels and the slips chained to them.
export type OrderLabelsBy =
  | 'none'
  | 'order_date_asc'
  | 'order_date_desc'
  | 'paid_date_asc'
  | 'paid_date_desc'
  | 'recipient_name_asc'
  | 'recipient_name_desc'
  | 'order_number_asc'
  | 'order_number_desc'
  | 'item_sku_asc'
  | 'item_sku_desc'
  | 'item_name_asc'
  | 'item_name_desc'
  | 'warehouse_location_asc'
  | 'warehouse_location_desc'

export type PackingSlipFormat =
  | '4x6'
  | 'A6'
  | 'A4'
  | '8.5x11_full'
  | '8.5x11_2_per_page'
  | '8.5x11_1_per_page'

export type SortItemsBy =
  | 'quantity_high_low'
  | 'quantity_low_high'
  | 'sku_asc'
  | 'name_asc'
  | 'warehouse_location'
  | 'none'

export type DocumentType =
  | 'label'
  | 'return_label'
  | 'packing_slip'
  | 'end_of_day'
  | 'other_forms'
  | 'order_summary'
  | 'pick_list'
  | 'shipment_manifest'
  | 'customs_form'

// ---------------------------------------------------------------------------
// Per-document-type routing preference. has_options drives whether the row
// renders a "Document Options" link in the table.
// ---------------------------------------------------------------------------

export type DocumentTypePref = {
  document_type: DocumentType
  print_to: PrintTo
  document_format_display: string
  has_options: boolean
}

// ---------------------------------------------------------------------------
// Sub-payloads — one struct per Document Options modal.
// ---------------------------------------------------------------------------

export type LabelLayoutSettings = {
  size: LabelSize
  layout: LabelLayout
  dpi: LabelDPI
  batch_summary: BatchSummaryOption
  order_labels_by: OrderLabelsBy
  print_store_logo: boolean
  label_messages: [string, string, string]
  shipping_cutoff_enabled: boolean
  shipping_cutoff_time: string // 'HH:MM AM/PM'
}

export type PackingSlipSettings = {
  format: PackingSlipFormat
  sort_items_by: SortItemsBy
  show_store_logo: boolean
  show_item_prices: boolean
  show_item_skus: boolean
  show_scan_to_view: boolean
  show_item_locations: boolean
  show_discounts: boolean
  show_zero_qty: boolean
  show_return_qr: boolean
}

export type OrderSummarySettings = {
  sort_orders_by: OrderLabelsBy
  hide_images: boolean
  use_product_sku: boolean
  hide_details: boolean
  include_scan_to_view: boolean
}

export type PrintWarningSettings = {
  reprint_packing_slips: boolean
  reprint_labels: boolean
}

// ---------------------------------------------------------------------------
// Aggregate payload — what GET /settings/printing returns and PATCH merges.
// ---------------------------------------------------------------------------

export type PrintingSettingsPayload = {
  document_types: DocumentTypePref[]
  label_layout: LabelLayoutSettings
  packing_slip: PackingSlipSettings
  order_summary: OrderSummarySettings
  print_warnings: PrintWarningSettings
}

// ---------------------------------------------------------------------------
// Display metadata — labels shown in the document type table. has_options
// matches the ShipStation reference (Label / Return Label / Packing Slip /
// Order Summary / Pick List / Shipment Manifest are configurable; End Of Day,
// Other Forms and Customs Form are routing-only).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Option dictionaries — single source of truth for dropdown enumerations.
// ---------------------------------------------------------------------------

export const PRINT_TO_OPTIONS: { value: PrintTo; label: string }[] = [
  { value: 'always_prompt', label: 'Always Prompt' },
  { value: 'default_printer', label: 'Default Printer' },
  { value: 'specific_printer', label: 'Specific Printer (mock)' },
]

export const LABEL_DPI_OPTIONS: { value: LabelDPI; label: string }[] = [
  { value: '203', label: '203 DPI' },
  { value: '300', label: '300 DPI' },
  { value: '600', label: '600 DPI' },
]

export const BATCH_SUMMARY_OPTIONS: { value: BatchSummaryOption; label: string }[] = [
  { value: 'none', label: 'No Summary' },
  { value: 'start', label: 'Summary at start' },
  { value: 'end', label: 'Summary at end' },
]

export const ORDER_LABELS_BY_OPTIONS: { value: OrderLabelsBy; label: string }[] = [
  { value: 'none', label: 'None (as selected)' },
  { value: 'order_date_asc', label: 'Order Date: oldest first' },
  { value: 'order_date_desc', label: 'Order Date: newest first' },
  { value: 'paid_date_asc', label: 'Paid Date: oldest first' },
  { value: 'paid_date_desc', label: 'Paid Date: newest first' },
  { value: 'recipient_name_asc', label: 'Recipient Name A-Z' },
  { value: 'recipient_name_desc', label: 'Recipient Name Z-A' },
  { value: 'order_number_asc', label: 'Order Number: low to high' },
  { value: 'order_number_desc', label: 'Order Number: high to low' },
  { value: 'item_sku_asc', label: 'Item SKU A-Z' },
  { value: 'item_sku_desc', label: 'Item SKU Z-A' },
  { value: 'item_name_asc', label: 'Item Name A-Z' },
  { value: 'item_name_desc', label: 'Item Name Z-A' },
  { value: 'warehouse_location_asc', label: 'Warehouse Location A-Z' },
  { value: 'warehouse_location_desc', label: 'Warehouse Location Z-A' },
]

export const ORDER_SUMMARY_SORT_OPTIONS = ORDER_LABELS_BY_OPTIONS

export const PACKING_SLIP_FORMAT_OPTIONS: { value: PackingSlipFormat; label: string }[] = [
  { value: '4x6', label: '4" x 6" (thermal)' },
  { value: 'A6', label: 'A6' },
  { value: '8.5x11_full', label: '8.5" x 11" (full page)' },
  { value: '8.5x11_2_per_page', label: '8.5" x 11" (2 per page)' },
  { value: 'A4', label: 'A4' },
]

export const SORT_ITEMS_BY_OPTIONS: { value: SortItemsBy; label: string }[] = [
  { value: 'quantity_high_low', label: 'Item Quantity (high to low)' },
  { value: 'quantity_low_high', label: 'Item Quantity (low to high)' },
  { value: 'sku_asc', label: 'SKU A-Z' },
  { value: 'name_asc', label: 'Name A-Z' },
  { value: 'warehouse_location', label: 'Warehouse Location' },
  { value: 'none', label: 'None' },
]

export const LABEL_SIZE_TABS: { value: LabelSize; label: string }[] = [
  { value: '4x6', label: '4" X 6"' },
  { value: '8.5x11', label: '8.5" X 11"' },
  { value: 'A4', label: 'A4' },
  { value: 'A6', label: 'A6' },
]
